import { Dimensions, fit, Offset } from 'fit-to-bounds'
import React, { useMemo } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import styled from 'styled-components'
import { ApplicationDataFull, ApplicationDataLuminary } from '../../../types/ApplicationData'
import { ConstelaConfiguration } from '../../../types/ConstelaConfiguration'
import ModuleData from '../../../types/ModuleData'
import { useSelector } from '../../store/hooks'
import Image from '../Image'
import PreviewLuminary from './PreviewLuminary'

export type ApplicationPreviewProps = {
  application: ApplicationDataFull
  className?: string
}

const PositionedPreviewLuminary: React.FC<{
  luminary: ApplicationDataLuminary
  offset: Offset
  scale: number
  configuration: ConstelaConfiguration
  availableModules: ModuleData[]
  stageSize: Dimensions
}> = ({ luminary, offset, scale, configuration, availableModules, stageSize }) => {
  const widthmm = useSelector((state) => state.data.system.width)
  const leftmm = (luminary.center ?? stageSize.width / 2) - (widthmm * luminary.scale) / 2
  const bottommm = luminary.bottom ?? 0

  const left = leftmm * scale + offset.left
  const bottom = bottommm * scale + offset.top
  const style = useMemo<React.CSSProperties>(
    () => ({
      position: 'absolute',
      left: left + 'px',
      bottom: bottom + 'px'
    }),
    [left, bottom]
  )

  return (
    <div style={style}>
      <PreviewLuminary
        availableModules={availableModules}
        configuration={configuration}
        scale={scale * luminary.scale}
      />
    </div>
  )
}

const ApplicationPreview = styled<React.FC<ApplicationPreviewProps>>(function ApplicationPreview({
  className,
  application
}) {
  const { width = 0, height = 0, ref } = useResizeDetector()

  const { size: scaledSize, offset, scale } = fit('cover', { width, height }, application.backgroundImage)
  const imageStyle = useMemo<React.CSSProperties>(
    () => ({
      width: scaledSize.width + 'px',
      height: scaledSize.height + 'px',
      marginTop: offset.top + 'px',
      marginLeft: offset.left + 'px'
    }),
    [offset.left, offset.top, scaledSize.height, scaledSize.width]
  )

  return (
    <div className={className} ref={ref}>
      <Image image={application.backgroundImage} responsive={false} style={imageStyle} />
      {application.luminaries.map((luminary, i) => (
        <PositionedPreviewLuminary
          key={'luminary-' + i}
          luminary={luminary}
          offset={offset}
          scale={scale}
          configuration={application.defaultConfiguration}
          availableModules={application.availableModules}
          stageSize={application.backgroundImage}
        />
      ))}
    </div>
  )
})`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`

export default ApplicationPreview
